import {useEffect, useLayoutEffect} from 'react'
import {isServer} from '../utils/utils'

/**
 * `useLayoutEffect` that is safe to use in server side rendered components.
 *
 * Layout effects never run on the server, so React logs a warning when a component using
 * `useLayoutEffect` is rendered with `renderToString`. This hook falls back to `useEffect`
 * on the server, where both are no-ops, and keeps the synchronous before-paint behaviour
 * in the browser.
 *
 * Use it for DOM reads and writes that must be applied before the browser paints, e.g.
 * positioning a scroll container, to avoid a visible flash of the unpositioned state.
 *
 * @type {typeof useLayoutEffect}
 *
 * @example
 * // position a scroll container before it is painted
 * const Carousel = ({initialIndex}) => {
 *   const [containerEl, setContainerEl] = useState(null)
 *
 *   useIsomorphicLayoutEffect(() => {
 *     containerEl?.scrollTo({left: initialIndex * containerEl.clientWidth, behavior: 'instant'})
 *   }, [containerEl, initialIndex])
 *
 *   return <div ref={setContainerEl} />
 * }
 */
const useIsomorphicLayoutEffect = isServer ? useEffect : useLayoutEffect

export default useIsomorphicLayoutEffect
